import React from 'react';
import { useLanguage } from '../i18n/LanguageContext';

const rules = {
  fr: { title: 'Règlement des tournois – Padel Mendyx', intro: 'Nos tournois offrent un maximum de temps de jeu, des matchs équilibrés et une ambiance conviviale grâce au classement évolutif.', sections: [['📋 Inscriptions', 'Il n’y a pas de catégories fixes visibles. Lors d’une première participation, le classement sélectionné sert de point de départ. Ensuite, le classement enregistré place automatiquement chaque équipe dans la poule la plus proche de son niveau réel.'], ['🎾 Déroulement', 'Chaque équipe joue au minimum 4 matchs : 3 matchs de poule et au moins 1 match de classement. Une équipe allant jusqu’en finale peut jouer 5 matchs. Chaque match se joue en un set gagnant de 6 jeux avec avantages.'], ['📊 Classement Padel Mendyx', 'Après chaque match, les joueurs gagnent ou perdent des points selon le résultat et la paire affrontée. Ce système affine le niveau et garantit des rencontres plus équilibrées.'], ['⏱ Durée', 'Un tournoi dure environ 4 heures et peut atteindre 4h30 pour les finales ou les matchs disputés.'], ['🏆 Récompenses', 'Vainqueurs : 2 × 25€ et participation offerte au tournoi des champions. Finalistes : 2 × 15€.']], privacy: 'Politique de confidentialité', legal: 'Mentions légales' },
  de: { title: 'Turnierregeln – Padel Mendyx', intro: 'Unsere Turniere bieten viel Spielzeit, ausgeglichene Begegnungen und eine freundliche Atmosphäre dank einer dynamischen Rangliste.', sections: [['📋 Anmeldung', 'Es gibt keine sichtbaren festen Kategorien. Beim ersten Turnier dient die gewählte Einstufung als Startwert. Danach platziert die gespeicherte Rangliste jedes Team automatisch in der passendsten Gruppe.'], ['🎾 Ablauf', 'Jedes Team spielt mindestens 4 Spiele: 3 Gruppenspiele und mindestens 1 Platzierungsspiel. Bis zum Finale sind 5 Spiele möglich. Gespielt wird ein Gewinnsatz bis 6 Spiele mit Vorteil.'], ['📊 Padel-Mendyx-Rangliste', 'Nach jedem Spiel werden abhängig von Ergebnis und Gegner Punkte gewonnen oder verloren. Dadurch werden Niveau und Begegnungen immer genauer.'], ['⏱ Dauer', 'Ein Turnier dauert etwa 4 Stunden, bei Finals oder engen Spielen bis zu 4,5 Stunden.'], ['🏆 Preise', 'Sieger: 2 × 25€ und kostenlose Teilnahme am Champions-Turnier. Finalisten: 2 × 15€.']], privacy: 'Datenschutz', legal: 'Impressum' },
  nl: { title: 'Toernooireglement – Padel Mendyx', intro: 'Onze toernooien bieden veel speeltijd, evenwichtige wedstrijden en een gezellige sfeer dankzij een evolutieve ranking.', sections: [['📋 Inschrijvingen', 'Er zijn geen zichtbare vaste categorieën. Bij een eerste deelname geldt de gekozen ranking als startpunt. Daarna plaatst de opgeslagen ranking elk team automatisch in de poule die het best bij het werkelijke niveau past.'], ['🎾 Verloop', 'Elk team speelt minimaal 4 wedstrijden: 3 poulewedstrijden en minstens 1 klassementswedstrijd. Wie de finale haalt, kan 5 wedstrijden spelen. Elke wedstrijd is één winnende set van 6 games met voordeel.'], ['📊 Padel Mendyx-ranking', 'Na elke wedstrijd winnen of verliezen spelers punten volgens het resultaat en de tegenstanders. Zo worden niveau en wedstrijden steeds nauwkeuriger en evenwichtiger.'], ['⏱ Duur', 'Een toernooi duurt ongeveer 4 uur en kan voor finales of spannende wedstrijden oplopen tot 4u30.'], ['🏆 Prijzen', 'Winnaars: 2 × € 25 en gratis deelname aan het kampioenentoernooi. Finalisten: 2 × € 15.']], privacy: 'Privacybeleid', legal: 'Wettelijke vermeldingen' },
  en: { title: 'Tournament rules – Padel Mendyx', intro: 'Our tournaments provide maximum playing time, balanced matches and a friendly atmosphere through an evolving ranking.', sections: [['📋 Registration', 'There are no visible fixed categories. For a first tournament, the selected ranking is the starting point. Afterwards, the recorded ranking automatically places each team in the pool closest to its actual level.'], ['🎾 Format', 'Each team plays at least 4 matches: 3 pool matches and at least 1 ranking match. A finalist may play 5 matches. Each match is one winning set of 6 games with advantage.'], ['📊 Padel Mendyx ranking', 'After every match, players gain or lose points according to the result and opponents. This continually improves level assessment and match balance.'], ['⏱ Duration', 'A tournament lasts about 4 hours and may reach 4.5 hours for finals or close matches.'], ['🏆 Prizes', 'Winners: 2 × €25 and free entry to the champions tournament. Finalists: 2 × €15.']], privacy: 'Privacy policy', legal: 'Legal notice' },
};

const ReglementTournoi = () => {
  const { language } = useLanguage();
  const c = rules[language] || rules.fr;
  return (
    <section className="bg-white text-black min-h-screen flex flex-col justify-between">
      <div className="w-full max-w-screen-lg mx-auto text-center flex-grow pt-24 pb-12 px-4 sm:px-6 md:px-10 lg:px-16">
        <h2 className="text-3xl md:text-4xl font-semibold text-[#4c2f8e] mb-10">
          {c.title}
        </h2>

        <div className="bg-[#f9f9f9] shadow-xl rounded-2xl border border-gray-300 p-6 md:p-10 text-left space-y-6">
          <p className="text-gray-700 text-base md:text-lg">{c.intro}</p>
          {c.sections.map(([title, text]) => <div key={title}><h3 className="text-xl md:text-2xl font-semibold text-[#4c2f8e] mb-3">{title}</h3><p className="text-gray-700 text-base md:text-lg leading-relaxed">{text}</p></div>)}
        </div>

        <div className="hidden">
          <div>
            <p className="text-gray-700 text-base md:text-lg leading-relaxed">
              Chez <strong>Padel Mendyx</strong>, nos tournois sont pensés pour offrir un maximum de temps de jeu,
              des matchs équilibrés et une ambiance conviviale. Grâce à notre système de classement évolutif,
              chaque équipe est placée au plus proche de son niveau réel pour garantir une expérience dynamique,
              accessible et compétitive.
            </p>
          </div>

          <div>
            <h3 className="text-xl md:text-2xl font-semibold text-[#4c2f8e] mb-4">
              📋 Les inscriptions
            </h3>
            <div className="space-y-4 text-gray-700 text-base md:text-lg leading-relaxed">
              <p>
                Lors des inscriptions, il n’y a pas de catégories fixes visibles comme dans les tournois classiques
                ({' '}<strong>P100</strong>, <strong>P250</strong>, etc.). C’est tout à fait normal.
              </p>
              <p>
                Grâce à notre plateforme et à notre <strong>classement évolutif</strong>, les équipes sont
                automatiquement placées dans des poules correspondant à leur <strong>niveau réel</strong>.
              </p>
              <p>
                Si c’est votre premier tournoi Padel Mendyx, vous commencez avec le classement que vous avez
                sélectionné et vous affrontez des équipes ayant un niveau le plus proche possible du vôtre.
              </p>
              <p>
                Si vous avez déjà participé à un tournoi, votre classement est déjà enregistré et nous vous plaçons
                directement dans la poule la plus adaptée à votre niveau.
              </p>
              <p>
                Ce système nous permet d’être plus précis que les catégories traditionnelles. Par exemple, dans un
                même tournoi, il peut y avoir plusieurs niveaux proches d’un <strong>P100</strong>, ce qui garantit
                des matchs plus équilibrés et plus intéressants.
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-xl md:text-2xl font-semibold text-[#4c2f8e] mb-4">
              🎾 Le déroulement du tournoi
            </h3>
            <div className="bg-white border border-gray-200 rounded-xl p-5 md:p-6 shadow-sm">
              <p className="text-gray-700 text-base md:text-lg mb-4 leading-relaxed">
                Chaque tournoi garantit au minimum <strong>4 matchs par équipe</strong> :
              </p>
              <ul className="text-gray-700 text-base md:text-lg list-disc list-inside space-y-2 mb-4">
                <li>3 matchs de poule</li>
                <li>
                  1 match de classement minimum
                  <span className="text-gray-600">
                    {' '}({`exemple : le 3ème de la poule 1 affronte le 3ème de la poule 2`})
                  </span>
                </li>
              </ul>
              <p className="text-gray-700 text-base md:text-lg mb-4 leading-relaxed">
                Si vous allez plus loin dans le tournoi, vous pouvez jouer jusqu’à la finale, ce qui peut représenter
                <strong> 5 matchs au total</strong>.
              </p>
              <p className="text-gray-700 text-base md:text-lg mb-4 leading-relaxed">
                Les matchs se jouent en <strong>un set gagnant de 6 jeux avec avantages</strong>.
              </p>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                Nous avons un ordre de matchs déjà établi : vous devez simplement donner votre résultat, et notre
                équipe s’occupe de toute la gestion du tournoi.
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-xl md:text-2xl font-semibold text-[#4c2f8e] mb-4">
              📊 Le classement Padel Mendyx
            </h3>
            <div className="space-y-4 text-gray-700 text-base md:text-lg leading-relaxed">
              <p>
                Après chaque match, vous <strong>gagnez ou perdez des points</strong> dans le classement Padel Mendyx
                selon votre résultat et la paire que vous affrontez.
              </p>
              <p>Ce système nous permet :</p>
              <ul className="list-disc list-inside space-y-2">
                <li>d’évaluer votre niveau</li>
                <li>de vous placer encore plus précisément dans les prochains tournois</li>
                <li>de vous garantir des matchs équilibrés à chaque participation</li>
              </ul>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-xl p-5 md:p-6 shadow-sm">
              <h3 className="text-xl md:text-2xl font-semibold text-[#4c2f8e] mb-3">
                ⏱ Durée du tournoi
              </h3>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                Un tournoi dure en moyenne <strong>4 heures</strong>, et peut aller jusqu’à
                <strong> 4h30</strong> pour les finales ou les matchs plus disputés.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 md:p-6 shadow-sm">
              <h3 className="text-xl md:text-2xl font-semibold text-[#4c2f8e] mb-3">
                🏆 Les récompenses
              </h3>
              <ul className="text-gray-700 text-base md:text-lg space-y-2">
                <li>
                  <strong>Vainqueurs :</strong> 2 x 25€ + participation au tournoi des champions offerte
                </li>
                <li>
                  <strong>Finalistes :</strong> 2 x 15€
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-[#f3effc] border border-[#d7caef] rounded-xl p-5 md:p-6">
            <p className="text-gray-800 text-base md:text-lg leading-relaxed">
              Rejoignez nos tournois et profitez d’une organisation fluide, de matchs équilibrés et d’un système
              conçu pour vous faire progresser tout en prenant du plaisir sur le terrain.
            </p>
          </div>
        </div>
      </div>

      <footer className="bg-white border-t border-[#4c2f8e] shadow-sm py-4 mt-8">
        <div className="w-full max-w-screen-lg mx-auto flex flex-col md:flex-row justify-between items-center px-6">
          <div className="flex justify-center mb-4 md:mb-0 space-x-4 flex-wrap gap-y-2">
            <a href="https://www.boisduloup.be/" target="_blank" rel="noopener noreferrer">
              <img
                src="/BDL.jfif"
                alt="Tennis Club Bois du Loup"
                className="h-10 w-10 transition-transform duration-300 hover:scale-105"
              />
            </a>
            <a href="https://www.facebook.com/RETCJUMET" target="_blank" rel="noopener noreferrer">
              <img
                src="/JUMET.jfif"
                alt="RETC Jumet"
                className="h-10 w-10 transition-transform duration-300 hover:scale-105"
              />
            </a>
            <a href="https://www.facebook.com/p/333-Padel-61556247308483/" target="_blank" rel="noopener noreferrer">
              <img
                src="/333.jfif"
                alt="333 Padel"
                className="h-10 w-10 rounded-full transition-transform duration-300 hover:scale-105"
              />
            </a>
            <a
              href="https://www.facebook.com/Centre.sportif.Adeps.La.Mosane/?locale=fr_FR"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="/adeps.jfif"
                alt="Adeps Le Mosane"
                className="h-10 w-10 rounded-full transition-transform duration-300 hover:scale-105"
              />
            </a>
            <a
              href="https://www.facebook.com/p/Hall-Omnisports-de-Grez-Doiceau-100063821912059/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="/grez.jfif"
                alt="Grez-Doiceau"
                className="h-10 w-10 rounded-full transition-transform duration-300 hover:scale-105"
              />
            </a>
            <a href="http://www.padel-one.be/" target="_blank" rel="noopener noreferrer">
              <img
                src="/padelone.jfif"
                alt="Padel One"
                className="h-10 w-10 rounded-full transition-transform duration-300 hover:scale-105"
              />
            </a>
          </div>

          <div className="flex space-x-4 text-sm text-gray-700">
            <a
              href="/privacy-policy"
              className="hover:text-[#4c2f8e] transition duration-300 hover:underline hover:underline-offset-4"
            >
              {c.privacy}
            </a>
            <a
              href="/legal-mentions"
              className="hover:text-[#4c2f8e] transition duration-300 hover:underline hover:underline-offset-4"
            >
              {c.legal}
            </a>
          </div>
        </div>
      </footer>
    </section>
  );
};

export default ReglementTournoi;
